/**
 * babyhyllan — lådan som spel. Behaviour for sections/bb-lada.liquid.
 *
 * The server renders the box's contents as a plain list. This element reads
 * that list and builds the scene from it: the closed box, the products that
 * rise out of it, the row of small icons that fills in as products are seen,
 * and the detail card. Nothing here invents content; every name, price and
 * fact comes from the list, so the scene and the list can never disagree.
 *
 * Adding to cart posts to the Ajax cart, plays the close-and-fly animation
 * while the request runs, and then announces the change with the same
 * CartLinesUpdateEvent the theme's own product form sends, so the cart icon
 * and the cart drawer update exactly as they do for any other add.
 *
 * Looking comes first, and buying is the next step it leads to: each
 * product says it comes with the box, once everything is seen the card
 * offers Klar, which closes it, and seeing everything or Klar makes the add
 * button shine once. A box that arrives from the shelf brings the bar with
 * the price up once it has opened.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

/* Positions above the box, in percent of the stage: [x, y, width]. Tuned by
   hand per count so labels never collide. */
const LAYOUTS = {
  1: [[50, 24, 32]],
  2: [[31, 28, 28], [69, 28, 28]],
  3: [[23, 33, 25], [50, 16, 25], [77, 33, 25]],
  4: [[19, 36, 25], [44, 16, 22], [69, 18, 22], [84, 42, 23]],
  5: [[13, 38, 20], [31, 19, 20], [50, 33, 20], [69, 19, 20], [87, 38, 20]],
  6: [[22, 15, 19], [50, 13, 19], [78, 15, 19], [15, 38, 18], [50, 36, 18], [85, 38, 18]],
};

/* Places on the shelves for products taken out of the box. */
const SLOTS = [
  { x: 9.5, y: 83, s: 0.5 },
  { x: 90.5, y: 83, s: 0.5 },
  { x: 9.5, y: 64.5, s: 0.44 },
  { x: 90.5, y: 64.5, s: 0.44 },
  { x: 9.5, y: 47, s: 0.4 },
  { x: 90.5, y: 47, s: 0.4 },
];

/* The middle of the box's mouth, in percent of the stage's height: the box
   stands on the shelf at 90 % and is the crate from bb-lada-mini, whose
   mouth is 31 % down a box 0.397 stages high (assets/bb-lada.css). */
const MOUTH_Y = 63;
const OPEN_MS = 1400;
const CLOSE_MS = 950;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const isWide = () => window.matchMedia('(min-width: 990px)').matches;

class BbLada extends HTMLElement {
  #abort = new AbortController();
  #timers = new Set();
  #busy = false;

  connectedCallback() {
    if (this.dataset.enhanced) return;

    this.stage = this.querySelector('[data-stage]');
    if (!this.stage) {
      document.querySelector('.bb-oppna[data-oppna-lada]')?.remove();
      return;
    }
    this.dataset.enhanced = 'true';

    try {
      this.config = JSON.parse(this.querySelector('[data-config]')?.textContent || '{}');
    } catch {
      this.config = {};
    }
    this.allowUnpack = this.dataset.allowUnpack === 'true';

    this.box = this.querySelector('[data-box]');
    this.itemsLayer = this.querySelector('[data-items]');
    this.openButton = this.querySelector('[data-open]');
    this.hint = this.querySelector('[data-hint]');
    this.seenLabel = this.querySelector('[data-seen]');
    this.collection = this.querySelector('[data-collection]');
    this.detail = this.querySelector('[data-detail]');
    this.priceEl = this.querySelector('[data-price]');
    this.noteEl = this.querySelector('[data-note]');
    this.doneEl = this.querySelector('[data-done]');
    this.cta = this.querySelector('[data-cta]');
    this.errorEl = this.querySelector('[data-error]');
    this.form = this.querySelector('[data-bar]');
    this.viewToggle = this.querySelector('[data-view-toggle]');
    this.klarButton = this.querySelector('[data-klar]');

    this.ctaText = this.cta?.textContent.trim() || '';
    this.noteHTML = this.noteEl?.innerHTML || '';

    this.items = [...this.querySelectorAll('[data-list] > .bb-lada__row')].map((row) => ({
      id: row.dataset.id,
      variant: row.dataset.variant,
      price: Number(row.dataset.price) || 0,
      short: row.dataset.short || '',
      available: row.dataset.available === 'true',
      isContainer: row.hasAttribute('data-container'),
      name: row.querySelector('.bb-lada__row-name')?.textContent.trim() || '',
      priceText: row.querySelector('.bb-lada__row-price')?.textContent.trim() || '',
      text: row.querySelector('.bb-lada__row-text')?.textContent.trim() || '',
      facts: row.querySelector('.bb-lada__facts'),
      url: row.querySelector('.bb-lada__link')?.getAttribute('href') || '#',
      art: row.querySelector('[data-art]'),
      row,
    }));
    if (!this.items.length) {
      document.querySelector('.bb-oppna[data-oppna-lada]')?.remove();
      return;
    }

    this.state = {
      phase: 'closed',
      selected: null,
      inBox: new Set(this.items.map((item) => item.id)),
      slotOf: new Map(),
      seen: new Set(),
      wasIncomplete: false,
      afterOpen: null,
    };

    this.#buildScene();
    this.#buildCollection();
    if (this.allowUnpack) this.#buildListToggles();
    this.#wire();

    this.querySelector('[data-scene-col]')?.removeAttribute('hidden');
    this.detail?.removeAttribute('hidden');
    this.classList.add('is-enhanced');

    this.#layout();
    this.#updatePrice(false);
    this.#renderDetail();
    this.#renderSeen();
    this.#watchForChat();
    this.#arrive();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#timers.forEach((id) => clearTimeout(id));
    this.#timers.clear();
  }

  /* ---------- building ---------- */

  #buildScene() {
    const floaters = this.items.filter((item) => !item.isContainer);
    const layout = LAYOUTS[Math.min(Math.max(floaters.length, 1), 6)];

    // The box is a picture, but the basket button goes inside it, so only
    // its own parts are hidden from screen readers, not the box as a whole.
    this.crate = this.box.querySelector('.bb-crate');
    if (this.crate) {
      this.crate.removeAttribute('aria-hidden');
      for (const part of this.crate.children) part.setAttribute('aria-hidden', 'true');
    }

    // The tissue paper goes into the box, over its mouth and under the lid.
    const mouth = this.crate?.querySelector('.bb-crate__mouth');
    const tissue = this.box.querySelector('.bb-lada__tissue');
    if (mouth && tissue) mouth.after(tissue);

    this.items.forEach((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.id = item.id;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', `${item.name}, ${item.priceText.replace(/^Styckpris\s*/, '')}`);
      button.tabIndex = -1;

      const art = item.art.cloneNode(true);
      art.removeAttribute('data-art');

      const tag = document.createElement('span');
      tag.className = 'bb-lada__tag';
      const dot = document.createElement('span');
      dot.className = 'bb-lada__dot';
      dot.setAttribute('aria-hidden', 'true');
      tag.append(dot, document.createTextNode(item.short));

      if (item.isContainer) {
        button.className = 'bb-lada__basket';
        button.append(art, tag);
        if (mouth) mouth.after(button);
        else this.box.append(button);
        // Most of the basket sits behind the box front, so a tap anywhere on
        // the open box chooses it. Keyboard users reach the button itself.
        const front = this.box.querySelector('.bb-crate__front');
        front?.classList.add('is-pickable');
        front?.addEventListener('click', (event) => {
          if (this.state.phase !== 'open') return;
          event.stopPropagation();
          this.#select(item.id);
        });
      } else {
        const index = floaters.indexOf(item);
        const [x, y, w] = layout[Math.min(index, layout.length - 1)];
        item.x = x;
        item.y = y;
        item.i = index;
        button.className = 'bb-lada__item';
        button.style.setProperty('--x', x);
        button.style.setProperty('--y', y);
        button.style.setProperty('--w', w);
        button.style.setProperty('--i', index);
        const float = document.createElement('span');
        float.className = 'bb-lada__float';
        float.append(art);
        button.append(float, tag);
        this.itemsLayer.append(button);
      }

      button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#select(item.id);
      });
      item.el = button;
    });
  }

  #buildCollection() {
    this.items.forEach((item) => {
      const li = document.createElement('li');
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'bb-lada__chip';
      chip.setAttribute('aria-label', item.name);
      const art = item.art.cloneNode(true);
      art.removeAttribute('data-art');
      chip.append(art);
      chip.addEventListener('click', () => this.#select(item.id));
      li.append(chip);
      this.collection.append(li);
      item.chip = chip;
    });
  }

  #buildListToggles() {
    this.items.forEach((item) => {
      const actions = item.row.querySelector('.bb-lada__row-actions');
      if (!actions) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bb-lada__pill bb-lada__pill--small';
      button.addEventListener('click', () => this.#toggleIn(item.id));
      actions.prepend(button);
      item.listToggle = button;
    });
    this.#renderList();
  }

  #wire() {
    const { signal } = this.#abort;

    this.openButton.addEventListener('click', () => this.#open(), { signal });
    this.stage.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (target === this.stage || target?.matches?.('[data-scene], [data-items]')) this.#deselect(false);
      },
      { signal }
    );
    this.querySelector('[data-detail-close]')?.addEventListener('click', () => this.#deselect(true), { signal });
    this.querySelector('[data-detail-toggle]')?.addEventListener(
      'click',
      () => this.state.selected && this.#toggleIn(this.state.selected),
      { signal }
    );
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.#step(-1), { signal });
    this.querySelector('[data-next]')?.addEventListener('click', () => this.#step(1), { signal });
    this.klarButton?.addEventListener('click', () => this.#finish(), { signal });
    this.viewToggle?.addEventListener('click', () => this.#setListView(!this.classList.contains('is-list-view')), {
      signal,
    });
    this.form?.addEventListener('submit', (event) => this.#onSubmit(event), { signal });

    document.addEventListener(
      'keydown',
      (event) => {
        if (!this.state.selected) return;
        if (event.target instanceof Element && event.target.closest('input, textarea, select')) return;
        if (event.key === 'Escape') this.#deselect(true);
        else if (event.key === 'ArrowRight') this.#step(1);
        else if (event.key === 'ArrowLeft') this.#step(-1);
      },
      { signal }
    );

    window.matchMedia('(min-width: 990px)').addEventListener(
      'change',
      () => {
        if (isWide()) this.detail?.classList.remove('is-open');
        else if (this.state.selected) this.detail?.classList.add('is-open');
      },
      { signal }
    );
  }

  /* ---------- state ---------- */

  #later(fn, ms) {
    const id = setTimeout(() => {
      this.#timers.delete(id);
      fn();
    }, ms);
    this.#timers.add(id);
    return id;
  }

  #wait(ms) {
    return new Promise((resolve) => this.#later(resolve, ms));
  }

  #setPhase(phase) {
    this.state.phase = phase;
    this.stage.dataset.state = phase;
  }

  #byId(id) {
    return this.items.find((item) => item.id === id);
  }

  #countIn() {
    return this.items.filter((item) => this.state.inBox.has(item.id)).length;
  }

  #layout() {
    const { phase, inBox, slotOf } = this.state;
    const floaters = this.items.filter((item) => !item.isContainer);
    const live = phase === 'open';

    floaters.forEach((item) => {
      const out = !inBox.has(item.id);
      let tx = 0;
      let ty = 0;
      let s = 1;
      let o = 1;
      let delay = 0;

      if (phase === 'opening' || phase === 'open') {
        if (out) {
          const slot = SLOTS[slotOf.get(item.id) ?? 0];
          tx = slot.x - item.x;
          ty = slot.y - item.y;
          s = slot.s;
        }
        if (phase === 'opening') delay = 0.62 + item.i * 0.08;
      } else if (out && phase === 'closing') {
        // What was taken out stays on the shelf while the box leaves.
        const slot = SLOTS[slotOf.get(item.id) ?? 0];
        tx = slot.x - item.x;
        ty = slot.y - item.y;
        s = slot.s;
      } else {
        tx = 50 - item.x;
        ty = MOUTH_Y - item.y;
        s = 0.3;
        o = 0;
        if (phase === 'closing') delay = (floaters.length - 1 - item.i) * 0.07;
      }

      const el = item.el;
      el.style.setProperty('--tx', `${tx.toFixed(2)}cqw`);
      el.style.setProperty('--ty', `${ty.toFixed(2)}cqh`);
      el.style.setProperty('--s', String(s));
      el.style.setProperty('--o', String(o));
      el.style.setProperty('--delay', `${delay}s`);
      el.classList.toggle('is-out', out && phase !== 'closed');
    });

    this.items
      .filter((item) => item.isContainer)
      .forEach((item) => item.el.classList.toggle('is-out', !inBox.has(item.id)));

    this.items.forEach((item) => {
      item.el.tabIndex = live ? 0 : -1;
      item.el.setAttribute('aria-hidden', live ? 'false' : 'true');
    });
    this.openButton.hidden = phase !== 'closed';
  }

  #open(thenSelect = null) {
    if (this.state.phase !== 'closed') return;
    this.state.afterOpen = thenSelect;
    this.#setPhase('opening');
    this.stage.classList.add('is-open');
    this.#layout();
    this.#say('');

    this.#later(() => {
      this.#setPhase('open');
      this.stage.classList.add('is-ready');
      this.#layout();
      const next = this.state.afterOpen;
      this.state.afterOpen = null;
      if (next) this.#select(next);
      else this.#updateHint();
    }, reducedMotion() ? 60 : OPEN_MS);
  }

  #select(id) {
    const { phase } = this.state;
    if (phase === 'closed') return this.#open(id);
    if (phase === 'opening') {
      this.state.afterOpen = id;
      return;
    }
    if (phase !== 'open' || this.classList.contains('is-list-view')) return;

    const wasOpen = this.detail?.classList.contains('is-open');
    const seenBefore = this.state.seen.size;
    this.state.selected = id;
    this.state.seen.add(id);
    this.stage.classList.add('has-selection');
    this.items.forEach((item) => {
      item.el.setAttribute('aria-pressed', String(item.id === id));
      item.chip.setAttribute('aria-current', String(item.id === id));
    });
    this.#renderDetail();
    this.#renderSeen();
    this.#say('');
    if (seenBefore < this.items.length && this.state.seen.size === this.items.length) this.#allSeen();

    if (!isWide() && this.detail) {
      this.detail.classList.add('is-open');
      if (!wasOpen) {
        this.stage.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
        this.querySelector('[data-detail-name]')?.focus({ preventScroll: true });
      }
    }
  }

  #deselect(returnFocus) {
    if (!this.state.selected) return;
    const previous = this.#byId(this.state.selected);
    this.state.selected = null;
    this.stage.classList.remove('has-selection');
    this.items.forEach((item) => {
      item.el.setAttribute('aria-pressed', 'false');
      item.chip.setAttribute('aria-current', 'false');
    });
    this.detail?.classList.remove('is-open');
    this.#renderDetail();
    this.#updateHint();
    if (returnFocus && previous && this.state.phase === 'open') previous.el.focus({ preventScroll: true });
  }

  #step(direction) {
    if (!this.state.selected) return;
    const index = this.items.indexOf(this.#byId(this.state.selected));
    const next = this.items[(index + direction + this.items.length) % this.items.length];
    this.#select(next.id);
  }

  /**
   * Klar, once everything is seen: the card closes and the add button is the
   * next thing in view, so it shines and takes the focus (without a ring
   * after a tap).
   */
  #finish() {
    this.#deselect(false);
    this.#shine();
    if (this.cta && !this.cta.disabled) this.cta.focus({ preventScroll: true });
  }

  /** Everything in the box seen: the row of small icons ripples and the add button shines. */
  #allSeen() {
    if (!reducedMotion()) {
      this.items.forEach((item, index) => {
        item.chip.animate([{ translate: '0 0' }, { translate: '0 -6px' }, { translate: '0 0' }], {
          duration: 460,
          delay: 120 + index * 70,
          easing: 'ease-in-out',
        });
      });
    }
    this.#shine();
  }

  /** A soft light across the add button, once, when it is the natural next step. */
  #shine() {
    const cta = this.cta;
    if (!cta || cta.disabled || this.#busy || reducedMotion()) return;
    cta.classList.remove('is-glans');
    void cta.offsetWidth;
    cta.classList.add('is-glans');
    this.#later(() => cta.classList.remove('is-glans'), 1300);
  }

  #toggleIn(id) {
    if (!this.allowUnpack) return;
    const { phase, inBox, slotOf } = this.state;
    if (phase === 'closing' || phase === 'opening') return;
    const item = this.#byId(id);

    if (inBox.has(id)) {
      if (!item.isContainer) {
        const used = new Set(slotOf.values());
        let free = 0;
        while (used.has(free)) free += 1;
        slotOf.set(id, Math.min(free, SLOTS.length - 1));
      }
      inBox.delete(id);
      this.state.wasIncomplete = true;
    } else {
      inBox.add(id);
      slotOf.delete(id);
    }

    this.#layout();
    this.#updatePrice(true);
    this.#renderDetail();
    this.#renderList();

    if (this.#countIn() === this.items.length && this.state.wasIncomplete) {
      this.state.wasIncomplete = false;
      this.#celebrate();
    } else if (this.doneEl) {
      this.doneEl.hidden = true;
    }
  }

  #celebrate() {
    if (this.doneEl) this.doneEl.hidden = false;
    this.#say('Allt på sin plats');
    const basket = this.items.find((item) => item.isContainer)?.el;
    if (basket && !reducedMotion()) {
      basket.animate([{ transform: 'none' }, { transform: 'translateY(-6%)' }, { transform: 'none' }], {
        duration: 520,
        easing: 'cubic-bezier(.25, 1.12, .4, 1)',
      });
    }
    this.#later(() => {
      if (this.doneEl) this.doneEl.hidden = true;
    }, 2800);
  }

  #setListView(on) {
    if (on) this.#deselect(false);
    this.classList.toggle('is-list-view', on);
    this.viewToggle.textContent = on ? 'Visa lådan' : 'Visa som lista';
    this.viewToggle.setAttribute('aria-pressed', String(on));
    this.#renderList();
  }

  /* ---------- rendering ---------- */

  #money(cents) {
    try {
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: this.config.currency || 'SEK',
        minimumFractionDigits: cents % 100 ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      return `${Math.round(cents / 100)} kr`;
    }
  }

  #lines() {
    if (this.#countIn() === this.items.length) {
      return [{ id: Number(this.config.boxVariant), quantity: 1 }];
    }
    return this.items
      .filter((item) => this.state.inBox.has(item.id))
      .map((item) => ({ id: Number(item.variant), quantity: 1 }));
  }

  #updatePrice(animate) {
    const inItems = this.items.filter((item) => this.state.inBox.has(item.id));
    const whole = inItems.length === this.items.length;
    const sum = inItems.reduce((total, item) => total + item.price, 0);
    const price = whole ? Number(this.config.boxPrice) || sum : sum;

    this.priceEl.textContent = this.#money(price);
    if (whole) {
      this.noteEl.innerHTML = this.noteHTML;
    } else if (inItems.length) {
      this.noteEl.textContent = `${inItems.length} av ${this.items.length} delar till styckpris. Lådpriset gäller när allt är med.`;
    } else {
      this.noteEl.textContent = 'Lådan är tom. Lägg tillbaka något först.';
    }

    const available = whole ? this.config.boxAvailable !== false : inItems.every((item) => item.available);
    if (!this.#busy) {
      this.cta.disabled = !inItems.length || !available;
      this.cta.textContent = available ? 'Lägg i varukorgen' : 'Slutsåld just nu';
    }

    if (animate && !reducedMotion()) {
      this.priceEl.classList.remove('is-ticking');
      void this.priceEl.offsetWidth;
      this.priceEl.classList.add('is-ticking');
    }
  }

  #renderDetail() {
    if (!this.detail) return;
    const item = this.state.selected ? this.#byId(this.state.selected) : null;
    this.querySelector('[data-detail-body]').hidden = !item;
    this.querySelector('[data-detail-empty]').hidden = Boolean(item);
    if (!item) return;

    const artSlot = this.querySelector('[data-detail-art]');
    const art = item.art.cloneNode(true);
    art.removeAttribute('data-art');
    artSlot.replaceChildren(art);

    this.querySelector('[data-detail-name]').textContent = item.name;
    // It comes with the box, so the button under the card buys the box, not
    // this product; its own price is for comparison.
    const inside = this.state.inBox.has(item.id);
    const badge = document.createElement('span');
    badge.className = `bb-lada__ingar${inside ? '' : ' is-ute'}`;
    badge.textContent = inside ? 'Ingår i lådan' : 'Utanför lådan';
    this.querySelector('[data-detail-price]').replaceChildren(badge, document.createTextNode(` ${item.priceText}`));
    const text = this.querySelector('[data-detail-text]');
    text.textContent = item.text;
    text.hidden = !item.text;

    const facts = this.querySelector('[data-detail-facts]');
    facts.replaceChildren(...(item.facts ? [...item.facts.children].map((node) => node.cloneNode(true)) : []));

    const toggle = this.querySelector('[data-detail-toggle]');
    toggle.hidden = !this.allowUnpack;
    toggle.textContent = this.state.inBox.has(item.id) ? 'Ta ur lådan' : 'Lägg tillbaka i lådan';

    this.querySelector('[data-detail-link]').setAttribute('href', item.url);
    const step = this.querySelector('[data-step]');
    step.textContent = `${this.items.indexOf(item) + 1} av ${this.items.length}`;

    // Everything seen: Klar takes the middle of the stepper, and the arrows
    // stay for another look.
    if (this.klarButton) {
      const done = this.state.seen.size === this.items.length;
      this.klarButton.hidden = !done;
      step.hidden = done;
    }
  }

  #renderSeen() {
    const count = this.state.seen.size;
    const total = this.items.length;
    this.seenLabel.textContent = count === total ? 'Du har sett allt i lådan' : `Sett ${count} av ${total}`;
    this.items.forEach((item) => {
      const seen = this.state.seen.has(item.id);
      item.el.classList.toggle('is-seen', seen);
      item.chip.classList.toggle('is-seen', seen);
    });
  }

  #renderList() {
    if (!this.allowUnpack) return;
    this.items.forEach((item) => {
      const inside = this.state.inBox.has(item.id);
      item.row.classList.toggle('is-out', !inside);
      if (item.listToggle) item.listToggle.textContent = inside ? 'Ta ur lådan' : 'Lägg tillbaka i lådan';
    });
  }

  #say(text) {
    if (this.hint) this.hint.textContent = text;
  }

  #updateHint() {
    const { phase, seen } = this.state;
    if (phase === 'open') {
      // Once everything is seen, the hint points on to the button, as long
      // as there is a box to buy (the row of icons says it is all seen).
      const canBuy = !!this.cta && !this.cta.disabled;
      if (seen.size < this.items.length) this.#say('Tryck på en sak i lådan');
      else this.#say(canBuy ? 'Allt ingår när du lägger lådan i varukorgen.' : '');
    } else {
      this.#say('');
    }
  }

  #watchForChat() {
    const check = () => {
      if (document.getElementById('shopify-chat') || document.querySelector('inbox-online-store-chat')) {
        this.classList.add('has-chat');
      }
    };
    check();
    this.#later(check, 1500);
    this.#later(check, 5000);
  }

  /* ---------- arriving from the shelf ---------- */

  /**
   * A box picked off the shelf on the home page arrives in the same
   * full-screen layer it left, in the middle of the screen (the section
   * shows the layer before the page is painted). From there it travels to
   * its place on the stage while the wall fades, the box on the stage takes
   * over where it lands, and it opens by itself. A tap, a key or a scroll
   * lands it at once.
   */
  async #arrive() {
    const layer = document.querySelector('.bb-oppna[data-oppna-lada]:not([hidden])');
    if (!(layer instanceof HTMLElement)) return;
    layer.classList.add('is-igang');

    const holder = layer.querySelector('.bb-oppna__lada');
    const flying = holder?.querySelector('.bb-crate');
    const wall = layer.querySelector('.bb-oppna__vagg');
    const title = layer.querySelector('.bb-oppna__titel');
    const animations = [];
    let skipped = false;

    const finish = () =>
      animations.forEach((animation) => {
        try {
          animation.finish();
        } catch {
          animation.cancel();
        }
      });
    const skip = () => {
      if (skipped) return;
      skipped = true;
      finish();
    };
    layer.addEventListener('pointerdown', skip);
    window.addEventListener('keydown', skip, { once: true });
    window.addEventListener('wheel', skip, { once: true, passive: true });
    window.addEventListener('touchmove', skip, { once: true, passive: true });

    const land = () => {
      this.box.style.removeProperty('visibility');
      layer.remove();
      document.documentElement.classList.remove('bb-oppnar');
      window.removeEventListener('keydown', skip);
      window.removeEventListener('wheel', skip);
      window.removeEventListener('touchmove', skip);
    };

    let barIn = null;
    try {
      if (!holder || !this.crate || reducedMotion()) return;
      this.openButton.hidden = true;
      this.box.style.visibility = 'hidden';

      // The bar with the price and the add button waits out of sight while
      // the box arrives and opens, and comes up as the next step once the
      // products are out. A timer brings it up regardless, so it is never
      // left hidden.
      if (this.form) {
        barIn = this.form.animate(
          isWide()
            ? [{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'none' }]
            : [{ transform: 'translateY(115%)' }, { transform: 'none' }],
          { duration: 620, easing: 'cubic-bezier(.25, 1.12, .4, 1)' }
        );
        barIn.pause();
        window.setTimeout(() => {
          if (barIn?.playState === 'paused') barIn.play();
        }, 5000);
      }

      // The theme's page transition first, so the journey is seen.
      if (window.bbOppnaVantar) {
        await Promise.race([window.bbOppnaVantar.catch(() => {}), this.#wait(500)]);
      }

      // Where the box will stand, in view.
      let to = this.crate.getBoundingClientRect();
      if (to.top < 0 || to.bottom > window.innerHeight) {
        this.stage.scrollIntoView({ block: 'center', behavior: 'instant' });
        to = this.crate.getBoundingClientRect();
      }
      const from = holder.getBoundingClientRect();
      const dx = to.left + to.width / 2 - (from.left + from.width / 2);
      const dy = to.top + to.height / 2 - (from.top + from.height / 2);
      const scale = to.width / from.width;

      const duration = 520;
      const travel = holder.animate(
        [{ transform: 'none' }, { transform: `translate(${dx}px, ${dy}px) scale(${scale})` }],
        { duration, easing: 'cubic-bezier(.5, 0, .25, 1)', fill: 'forwards' }
      );
      animations.push(travel);
      if (wall) {
        animations.push(
          wall.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 420, delay: 100, easing: 'ease', fill: 'forwards' })
        );
      }
      if (title) {
        animations.push(
          title.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(8px)' }], {
            duration: 180,
            easing: 'ease-in',
            fill: 'forwards',
          })
        );
      }
      if (flying) {
        animations.push(
          flying.animate([{ filter: 'drop-shadow(0 18px 24px rgb(67 48 46 / 0.16))' }, { filter: 'none' }], {
            duration,
            fill: 'forwards',
          })
        );
      }
      if (skipped) finish();
      // The last tenth of the way is too small to see; the lid comes off
      // there instead of after a pause.
      await Promise.race([travel.finished.catch(() => {}), this.#wait(duration * 0.9)]);
      finish();
    } finally {
      land();
    }

    if (reducedMotion()) return;
    // Straight on from the landing: the lid comes off without the pause a
    // tap on a waiting box gets.
    this.stage.classList.add('is-ankomst');
    this.#open();
    this.#later(() => this.stage.classList.remove('is-ankomst'), OPEN_MS);
    // The bar comes up as the last products rise out of the box.
    this.#later(() => {
      if (barIn?.playState === 'paused') barIn.play();
    }, 950);
  }

  /* ---------- cart ---------- */

  async #onSubmit(event) {
    event.preventDefault();
    if (this.#busy) return;
    const { phase } = this.state;
    if (phase === 'opening' || phase === 'closing') return;

    const lines = this.#lines();
    if (!lines.length) return;

    this.#busy = true;
    this.cta.disabled = true;
    // Says what is happening while the box closes and flies, in its own
    // colour: a greyed-out button would look sold out.
    this.cta.classList.remove('is-glans');
    this.cta.classList.add('is-packar');
    this.cta.textContent = 'Packar lådan…';
    if (this.errorEl) this.errorEl.hidden = true;

    const request = this.#postToCart(lines);
    await this.#closeAndFly();
    const result = await request;
    this.cta.classList.remove('is-packar');

    if (result.ok) {
      this.#announce(lines, result.sections);
      this.cta.classList.add('is-tillagd');
      this.cta.textContent = 'I varukorgen';
      this.#later(() => this.#reset(), 1400);
    } else {
      if (this.errorEl) {
        this.errorEl.textContent = result.message || 'Det gick inte att lägga lådan i varukorgen. Försök igen.';
        this.errorEl.hidden = false;
      }
      this.#reset();
    }
  }

  async #postToCart(lines) {
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);

    try {
      const response = await fetch(this.config.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: lines,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const json = await response.json();
      if (!response.ok || json.status) return { ok: false, message: json.description || json.message };
      return { ok: true, sections: json.sections };
    } catch {
      return { ok: false };
    }
  }

  /* Tell the theme, the way its own product form does, so the cart icon and
     the cart drawer refresh. Sent after the box has landed in the icon. */
  async #announce(lines, sections) {
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${this.config.cartUrl || '/cart'}.json`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bb-lada',
          sourceId: this.closest('.shopify-section')?.id || 'bb-lada',
          itemCount: lines.reduce((total, line) => total + line.quantity, 0),
          sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
    }
  }

  async #closeAndFly() {
    const wasClosed = this.state.phase === 'closed';
    this.#deselect(false);
    this.#setPhase('closing');
    this.stage.classList.remove('is-open', 'is-ready');
    this.#layout();
    this.#say('');

    if (!wasClosed && !reducedMotion()) await this.#wait(CLOSE_MS);

    const target = document.querySelector('cart-icon') || document.querySelector('a[href$="/cart"]');
    const from = this.box.getBoundingClientRect();
    const to = target?.getBoundingClientRect();

    let animation;
    if (reducedMotion() || !to || !to.width) {
      animation = this.box.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, fill: 'forwards' });
    } else {
      const dx = to.left + to.width / 2 - (from.left + from.width / 2);
      const dy = to.top + to.height / 2 - (from.top + from.height / 2);
      this.stage.style.zIndex = '60';
      animation = this.box.animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1 },
          { transform: `translate(${dx * 0.3}px, ${dy * 0.3 - 50}px) scale(.72)`, opacity: 1, offset: 0.4 },
          { transform: `translate(${dx}px, ${dy}px) scale(.06)`, opacity: 0.3 },
        ],
        { duration: 880, easing: 'cubic-bezier(.45, 0, .25, 1)', fill: 'forwards' }
      );
    }
    await animation.finished.catch(() => {});
    this.box.style.visibility = 'hidden';
    this.stage.style.zIndex = '';
  }

  #reset() {
    this.box.getAnimations().forEach((animation) => animation.cancel());
    this.box.style.visibility = '';
    this.state.inBox = new Set(this.items.map((item) => item.id));
    this.state.slotOf.clear();
    this.state.wasIncomplete = false;
    if (this.doneEl) this.doneEl.hidden = true;

    this.#setPhase('closed');
    this.stage.classList.remove('is-open', 'is-ready', 'has-selection');
    this.#layout();
    if (!reducedMotion()) {
      this.box.animate([{ transform: 'translateY(-14%)', opacity: 0 }, { transform: 'none', opacity: 1 }], {
        duration: 480,
        easing: 'cubic-bezier(.25, 1.12, .4, 1)',
      });
    }

    this.#busy = false;
    this.cta.classList.remove('is-packar', 'is-tillagd');
    this.#updatePrice(false);
    this.#renderList();
    this.#updateHint();
  }
}

if (!customElements.get('bb-lada')) {
  customElements.define('bb-lada', BbLada);
}
